import { useTranslation } from "react-i18next";
import colors from "tailwindcss/colors";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import Card from "@components/zenith/ui/Card";
import ChartDataTable from "@components/zenith/ui/ChartDataTable";
import Loading from "@components/shared/Loading";
import { useSubscriberDeclarations } from "@hooks/query/zenith/useSubscriberDeclarations";
import {
  GRID_PROPS,
  LEGEND_PROPS,
  X_AXIS_PROPS,
  Y_AXIS_PROPS,
  monthlySeries,
  netDeclarations,
} from "@components/zenith/ui/chartSeries";
import { formatVolume } from "@utils/formatVolume";
import { useDashboardPeriod } from "./useDashboardPeriod";

/** Monthly usage per zone over the accounting period, stacked. Volumes are in litres. */
const DeclarationChartWidget: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { accountingPeriod, isPeriodLoading, isPeriodFetching, periodNotice } =
    useDashboardPeriod();
  const {
    data: declarations,
    isLoading: isDeclarationsLoading,
    isFetching: isDeclarationsFetching,
  } = useSubscriberDeclarations({
    // The API lists the active period's readings only, so no month repeats.
    select: (data: any) =>
      netDeclarations(data?.filter((d: any) => !d.deletedAt) ?? []),
  });

  if (isDeclarationsLoading || isPeriodLoading) {
    return (
      <div className="pt-20">
        <Loading />
      </div>
    );
  }

  const {
    series: bars,
    colorOf,
    dataset,
  } = monthlySeries(
    declarations ?? [],
    accountingPeriod?.periodStart,
    ({ proportions }) => proportions.flatMap(Object.entries),
    t("common.other"),
    key => t(key),
  );
  // Past loading, no readings at all means they failed to load; a failed
  // refresh keeps the last ones.
  const notice =
    periodNotice ??
    (bars.length > 0
      ? undefined
      : !declarations
        ? t("zenith.dashboard.usage_unavailable")
        : t("declaration.no_data"));

  return (
    <Card
      className="min-w-0"
      title={t("subscriber.volume_used_widget_title")}
      busy={isDeclarationsFetching || isPeriodFetching}
      action={
        <span className="text-xs text-gray-500">{t("common.volume_unit")}</span>
      }
    >
      {notice ? (
        <p className="my-16 text-center text-lg font-light text-primary-2">
          {notice}
        </p>
      ) : (
        <div className="h-72 px-4 pb-4 pt-5">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dataset} margin={{ left: -8, right: 8 }}>
              <CartesianGrid {...GRID_PROPS} />
              <XAxis {...X_AXIS_PROPS} />
              <YAxis {...Y_AXIS_PROPS} />
              <Tooltip
                cursor={{ fill: colors.gray[100] }}
                formatter={(value: number) => formatVolume(value)}
              />
              <Legend {...LEGEND_PROPS} />
              {bars.map((zone, i) => (
                <Bar
                  key={zone}
                  dataKey={zone}
                  stackId="usage"
                  fill={colorOf(zone, i)}
                  stroke={colors.white}
                  strokeWidth={1}
                  maxBarSize={24}
                  radius={i === bars.length - 1 ? [4, 4, 0, 0] : 0}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
          <ChartDataTable
            label={t("subscriber.volume_used_widget_title")}
            series={bars}
            dataset={dataset}
          />
        </div>
      )}
    </Card>
  );
};

export default DeclarationChartWidget;
